import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { KibButtonNew } from '@chewy/kib-controls-react'
import { KibAddIcon, KibChewyIcon } from '@chewy/kib-icons-react'
import { KibSpinner } from '@chewy/kib-interstitials-react'
import { Text, Utils } from '@pbt/pbt-ui-components'

import { AutoshipInfo } from '~/components/common/AutoshipInfo'
import ClientAndPatient from '~/components/common/ClientAndPatient'
import type { AutoshipFrequencyOption } from '~/constants/autoship'
import { getAutoshipUnit } from '~/store/reducers/constants'
import type { Prescription } from '~/types/entities/prescriptions'
import {
  getPrescriptionAutoshipFrequency,
  getPrescriptionAutoshipUnitId,
} from '~/utils/prescription'

import {
  type SwapProductDialogInitialStep,
  SwapProductDialogSteps,
} from '../types'
import { FinalizePrescriptionErrorToast } from './FinalizePrescriptionErrorToast'

import styles from './FinalizePrescriptionStep.module.scss'

interface FinalizePrescriptionStepProps {
  hasError: boolean
  initialStep: SwapProductDialogInitialStep
  isSwapLoading: boolean
  onAddItem: () => void
  onDeletePrescription?: (index: number) => void
  onEditPrescription?: (index: number) => void
  onProceed?: () => void
  prescriptions: Prescription[]
}

export function FinalizePrescriptionStep({
  initialStep,
  hasError,
  isSwapLoading,
  onDeletePrescription,
  onEditPrescription,
  onAddItem,
  onProceed,
  prescriptions,
}: FinalizePrescriptionStepProps) {
  const { t } = useTranslation(['Common', 'Dialogs', 'Invoices'])
  const AutoshipUnit = useSelector(getAutoshipUnit)

  const showClientAndPatient =
    prescriptions.length > 0 &&
    Boolean(prescriptions[0]?.clientId) &&
    Boolean(prescriptions[0]?.patientId)

  const confirmButtonStrings = {
    [SwapProductDialogSteps.CHANGE_PRODUCT]: t(
      'Dialogs:SWAP_PRODUCT_DIALOG.CONFIRM_PRODUCT_CHANGE',
    ),
    [SwapProductDialogSteps.CHANGE_PATIENT]: t(
      'Dialogs:SWAP_PRODUCT_DIALOG.CONFIRM_PATIENT_CHANGE',
    ),
    [SwapProductDialogSteps.EDIT_RX]: t(
      'Dialogs:SWAP_PRODUCT_DIALOG.CONFIRM_COUNT_CHANGE',
    ),
  }

  return (
    <div>
      {hasError && <FinalizePrescriptionErrorToast />}
      <div className={styles.chewyCell}>
        <KibChewyIcon className={styles.chewyIcon} />
        {t('Invoices:CHEWY_ITEMS.TITLE')}
      </div>
      {showClientAndPatient && prescriptions[0] && (
        <ClientAndPatient
          loadClient
          clientId={prescriptions[0].clientId}
          patientId={prescriptions[0].patientId}
          textVariant="body2"
        />
      )}
      <div className={styles.prescriptionItemsContainer}>
        {prescriptions.map((prescription, index) => {
          const autoshipUnitId = getPrescriptionAutoshipUnitId(prescription)
          const autoshipFrequency =
            getPrescriptionAutoshipFrequency(prescription)
          const showAutoshipInfo = autoshipFrequency && autoshipUnitId

          return (
            // eslint-disable-next-line react/no-array-index-key
            <div className={styles.prescriptionItem} key={index}>
              <div>
                <Text>{prescription.name}</Text>
                {showAutoshipInfo && (
                  <AutoshipInfo
                    frequency={
                      {
                        uom: Utils.getConstantName(
                          autoshipUnitId,
                          AutoshipUnit,
                        ),
                        value: autoshipFrequency,
                      } as AutoshipFrequencyOption
                    }
                  />
                )}
              </div>
              <div className={styles.actions}>
                <button
                  className={styles.linkButton}
                  type="button"
                  onClick={() => onEditPrescription?.(index)}
                >
                  {t('Dialogs:SWAP_PRODUCT_DIALOG.EDIT_PRODUCT')}
                </button>
                {prescriptions.length > 1 && (
                  <>
                    <span className={styles.separator}>|</span>
                    <button
                      className={styles.linkButton}
                      type="button"
                      onClick={() => onDeletePrescription?.(index)}
                    >
                      {t('Common:DELETE_ACTION')}
                    </button>
                  </>
                )}
              </div>
            </div>
          )
        })}
      </div>
      <KibButtonNew
        emphasis="secondary"
        icon={<KibAddIcon />}
        size="medium"
        onClick={onAddItem}
      >
        {t('Dialogs:SWAP_PRODUCT_DIALOG.ADD_ANOTHER_ITEM')}
      </KibButtonNew>
      <div className={styles.footer}>
        <KibButtonNew
          icon={isSwapLoading && <KibSpinner />}
          loading={isSwapLoading}
          onClick={onProceed}
        >
          {confirmButtonStrings[initialStep]}
        </KibButtonNew>
      </div>
    </div>
  )
}
